import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { fmtMd, fmtDayDate, fmtTime, fmtTimeSec, fmtDateTime, fmtDateShort, todayIST, daysAgoIST } from '../utils/time';
import DailyReportScheduleModal from '../components/DailyReportScheduleModal';

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev') ? 'https://guard-patrol-73go.onrender.com' : '');

export default function Reports() {
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'trends'

  // Daily report states
  const [selectedDate, setSelectedDate] = useState(todayIST());
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [expandedPatrolId, setExpandedPatrolId] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const getImgSrc = (item) => {
    if (!item) return null;
    if (item.image_base64) {
      return item.image_base64.startsWith('data:')
        ? item.image_base64
        : `data:image/jpeg;base64,${item.image_base64}`;
    }
    if (item.image_url) {
      if (item.image_url.startsWith('http')) return item.image_url;
      return `${API_BASE}${item.image_url}`;
    }
    return null;
  };

  // 30-day trends states
  const [trendsData, setTrendsData] = useState(null);
  const [from, setFrom] = useState(daysAgoIST(30));
  const [to, setTo] = useState(todayIST());
  const [trendsLoading, setTrendsLoading] = useState(false);

  // Load daily report
  const loadDailyReport = async (date = selectedDate) => {
    setDailyLoading(true);
    try {
      const res = await axios.get(`/api/reports/daily?date=${date}`);
      setDailyData(res.data);
    } catch (err) {
      toast.error('Failed to load daily report data');
    } finally {
      setDailyLoading(false);
    }
  };

  // Load trends summary
  const loadTrends = async () => {
    setTrendsLoading(true);
    try {
      const res = await axios.get(`/api/reports/summary?from=${from}&to=${to}`);
      setTrendsData(res.data);
    } catch (err) {
      toast.error('Failed to load trends data');
    } finally {
      setTrendsLoading(false);
    }
  };

  useEffect(() => {
    loadDailyReport();
    loadTrends();
  }, []);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setSelectedDate(d);
    loadDailyReport(d);
  };

  const handleSendEmailNow = async () => {
    setSendingEmail(true);
    try {
      const res = await axios.post('/api/reports/daily/send-email', {
        date: selectedDate,
      });
      toast.success(res.data.message || `Daily report for ${selectedDate} sent via email!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send email. Check SMTP settings.');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedPatrolId(prev => (prev === id ? null : id));
  };

  const handleDownloadPdf = () => {
    const url = `${API_BASE}/api/reports/public/daily-pdf-download?date=${selectedDate}`;
    window.open(url, '_blank');
  };

  const handleOpenPdfReport = () => {
    const url = `${API_BASE}/api/reports/public/daily-pdf?date=${selectedDate}&print=true`;
    window.open(url, '_blank');
  };

  const handleCopyPublicLink = () => {
    const url = `${API_BASE}/api/reports/public/daily-pdf?date=${selectedDate}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success('📋 Public PDF link copied! Share with managers to view without login.');
    } else {
      toast.success(`Report Link: ${url}`);
    }
  };

  return (
    <div>
      {/* Top Header */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <div className="page-title">📋 Patrol Reports & Compliance</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Review completed patrols, missed checkpoints, security logs, and automated email dispatches (IST / Asia:Kolkata)
          </div>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm"
            style={{
              background: '#f0fdf4',
              border: '1.5px solid #16a34a',
              color: '#15803d',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onClick={() => setShowScheduleModal(true)}
          >
            ⏰ Daily Email Schedule
          </button>

          {activeTab === 'daily' && (
            <>
              <button
                type="button"
                className="btn btn-sm"
                style={{
                  background: '#0f172a',
                  color: '#38bdf8',
                  border: '1.5px solid #38bdf8',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 8px rgba(15,23,42,0.18)'
                }}
                onClick={handleOpenPdfReport}
                title="Open executive PDF summary with photos (can be saved as PDF)"
              >
                📄 View & Print PDF (with Photos)
              </button>

              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleCopyPublicLink}
                title="Copy link for managers to check on mobile/WhatsApp without login"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                🔗 Copy Manager Link
              </button>

              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleDownloadPdf}
                title="Download physical .pdf file directly"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                📥 Download .PDF
              </button>

              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={sendingEmail || dailyLoading}
                onClick={handleSendEmailNow}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                📧 {sendingEmail ? 'Sending Email...' : 'Send Report Email Now'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Switcher */}
      <div style={{
        display: 'flex',
        gap: 8,
        borderBottom: '2px solid #e2e8f0',
        marginBottom: 20
      }}>
        <button
          type="button"
          onClick={() => setActiveTab('daily')}
          style={{
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            color: activeTab === 'daily' ? '#2563eb' : '#64748b',
            borderBottom: activeTab === 'daily' ? '3px solid #2563eb' : '3px solid transparent',
            marginBottom: -2,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          🛡️ Daily Patrol & Missed Checkpoints
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('trends')}
          style={{
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            color: activeTab === 'trends' ? '#2563eb' : '#64748b',
            borderBottom: activeTab === 'trends' ? '3px solid #2563eb' : '3px solid transparent',
            marginBottom: -2,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          📈 30-Day Trends & Analytics
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: DAILY PATROLS & MISSED CHECKPOINTS
          ───────────────────────────────────────────────────────────── */}
      {activeTab === 'daily' && (
        <div>
          {/* Date Selector Filter Bar */}
          <div className="card mb-4" style={{ padding: '12px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Select Report Date:</span>
                <input
                  type="date"
                  className="form-input"
                  style={{ width: 160, fontWeight: 600 }}
                  value={selectedDate}
                  onChange={handleDateChange}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const today = todayIST();
                    setSelectedDate(today);
                    loadDailyReport(today);
                  }}
                >
                  Today
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const yesterday = daysAgoIST(1);
                    setSelectedDate(yesterday);
                    loadDailyReport(yesterday);
                  }}
                >
                  Yesterday
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Showing: <strong>{fmtDayDate(selectedDate)}</strong> (IST)
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => loadDailyReport(selectedDate)}
                  disabled={dailyLoading}
                >
                  🔄 {dailyLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          {dailyLoading && !dailyData && (
            <div style={{ textAlign: 'center', padding: 50, color: '#64748b' }}>
              Loading Daily Report for {selectedDate}...
            </div>
          )}

          {dailyData && (
            <>
              {/* Daily Executive KPI Cards */}
              <div className="stats-grid mb-6">
                <div className="card">
                  <div className="card-title">Total Patrols</div>
                  <div className="card-value" style={{ color: '#0284c7' }}>
                    {dailyData.summary?.total_patrols || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {dailyData.summary?.completed_patrols || 0} completed successfully
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">Avg Compliance Rate</div>
                  <div
                    className="card-value"
                    style={{
                      color: (dailyData.summary?.avg_compliance || 0) >= 80 ? '#16a34a' : '#dc2626'
                    }}
                  >
                    {dailyData.summary?.avg_compliance || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    Across all routes on {fmtDayDate(selectedDate)}
                  </div>
                </div>

                <div className="card" style={{
                  border: dailyData.summary?.totalMissedCount > 0 ? '1.5px solid #fecdd3' : '1px solid #e2e8f0',
                  background: dailyData.summary?.totalMissedCount > 0 ? '#fff1f2' : '#ffffff'
                }}>
                  <div className="card-title" style={{ color: dailyData.summary?.totalMissedCount > 0 ? '#9f1239' : '#64748b' }}>
                    Missed Checkpoints
                  </div>
                  <div
                    className="card-value"
                    style={{
                      color: dailyData.summary?.totalMissedCount > 0 ? '#e11d48' : '#16a34a'
                    }}
                  >
                    {dailyData.summary?.totalMissedCount || 0}
                  </div>
                  <div style={{ fontSize: 12, color: dailyData.summary?.totalMissedCount > 0 ? '#be123c' : '#64748b', marginTop: 4 }}>
                    {dailyData.summary?.totalMissedCount > 0 ? 'Action required: CPs unverified' : '100% Checkpoint Coverage'}
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">Security Incidents</div>
                  <div
                    className="card-value"
                    style={{
                      color: (dailyData.incidents?.length || 0) > 0 ? '#ea580c' : '#16a34a'
                    }}
                  >
                    {dailyData.incidents?.length || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    Reported on {selectedDate}
                  </div>
                </div>
              </div>

              {/* ⚠️ MISSED CHECKPOINTS HIGHLIGHT CALLOUT */}
              {dailyData.summary?.totalMissedCount > 0 ? (
                <div style={{
                  background: '#fff1f2',
                  border: '1.5px solid #fecdd3',
                  borderRadius: 12,
                  padding: '16px 20px',
                  marginBottom: 24,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#9f1239' }}>
                      Attention: {dailyData.summary.totalMissedCount} Checkpoint(s) Were Missed on {selectedDate}
                    </h3>
                  </div>
                  <div style={{ fontSize: 13, color: '#881337', marginBottom: 12 }}>
                    The following patrols missed scheduled checkpoints. Review each checkpoint name and scan method below:
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {dailyData.patrols
                      .filter(p => p.missed && p.missed.length > 0)
                      .map(p => (
                        <div
                          key={p.id}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #fca5a5',
                            borderRadius: 8,
                            padding: '12px 16px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
                              📍 {p.route_name} <span style={{ color: '#64748b', fontWeight: 400 }}>({p.site_name})</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#475569' }}>
                              Guard: <strong>{p.guard_name}</strong> • Time: <strong>{p.started_at ? fmtTime(p.started_at) : '--'} IST</strong>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {p.missed.map(cp => (
                              <div
                                key={cp.checkpoint_id}
                                style={{
                                  background: '#fee2e2',
                                  border: '1px solid #f87171',
                                  borderRadius: 6,
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  color: '#991b1b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <span style={{ fontWeight: 700 }}>#{cp.sequence_order}</span>
                                <span>{cp.name}</span>
                                <span style={{ fontSize: 10, background: '#fca5a5', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
                                  {cp.scan_type || 'QR'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : dailyData.summary?.total_patrols > 0 ? (
                <div style={{
                  background: '#f0fdf4',
                  border: '1.5px solid #bbf7d0',
                  borderRadius: 12,
                  padding: '14px 20px',
                  marginBottom: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <span style={{ fontSize: 22 }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#166534', fontSize: 14 }}>
                      100% Checkpoint Verification Achieved
                    </div>
                    <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>
                      All active checkpoints scheduled on patrols for {selectedDate} were successfully scanned and verified.
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Patrols Detail Table */}
              <div className="card mb-6">
                <div style={{
                  padding: '14px 18px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                    🛡️ Completed & Active Patrols ({dailyData.patrols?.length || 0})
                  </div>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    All times displayed in Indian Standard Time (IST / UTC+5:30)
                  </span>
                </div>

                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Guard</th>
                        <th>Route & Site</th>
                        <th>Started / Ended (IST)</th>
                        <th>Status</th>
                        <th>Compliance</th>
                        <th>Checkpoints</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyData.patrols?.map(p => {
                        const isExpanded = expandedPatrolId === p.id;
                        const missedCount = p.missed?.length || 0;
                        const verifiedCount = p.verified?.length || 0;

                        return (
                          <React.Fragment key={p.id}>
                            <tr style={{ background: isExpanded ? '#f8fafc' : undefined }}>
                              <td>
                                <strong>{p.guard_name}</strong>
                                {p.guard_email && (
                                  <div style={{ fontSize: 11, color: '#64748b' }}>{p.guard_email}</div>
                                )}
                              </td>
                              <td>
                                <strong>{p.route_name}</strong>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{p.site_name}</div>
                              </td>
                              <td style={{ fontSize: 12 }}>
                                <div>
                                  <strong>{p.started_at ? fmtTime(p.started_at) : '--'}</strong>
                                  {' – '}
                                  {p.ended_at ? fmtTime(p.ended_at) : <span style={{ color: '#d97706', fontWeight: 600 }}>Active</span>}
                                </div>
                                {p.duration_minutes !== null && (
                                  <div style={{ color: '#64748b', fontSize: 11 }}>({p.duration_minutes} mins)</div>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${
                                  p.status === 'completed' ? 'badge-success' :
                                  p.status === 'active' ? 'badge-warning' : 'badge-danger'
                                }`}>
                                  {p.status}
                                </span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{
                                    fontWeight: 700,
                                    color: (p.compliance_percent || 0) >= 80 ? '#16a34a' : '#dc2626'
                                  }}>
                                    {p.compliance_percent || 0}%
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span style={{ color: '#16a34a', fontWeight: 600 }}>{verifiedCount} verified</span>
                                {missedCount > 0 ? (
                                  <span style={{ color: '#dc2626', fontWeight: 700, marginLeft: 6 }}>
                                    ({missedCount} missed!)
                                  </span>
                                ) : (
                                  <span style={{ color: '#64748b', fontSize: 11, marginLeft: 6 }}>(0 missed)</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className={`btn btn-sm ${isExpanded ? 'btn-primary' : 'btn-secondary'}`}
                                  onClick={() => toggleExpand(p.id)}
                                  style={{ fontSize: 11 }}
                                >
                                  {isExpanded ? 'Hide Details ▲' : 'View Checkpoints ▼'}
                                </button>
                              </td>
                            </tr>

                            {/* Expanded Checkpoint Breakdown Row */}
                            {isExpanded && (
                              <tr style={{ background: '#f8fafc' }}>
                                <td colSpan={7} style={{ padding: '16px 20px' }}>
                                  <div style={{
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 10,
                                    padding: '16px 20px'
                                  }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                      {/* Verified List */}
                                      <div>
                                        <div style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: '#15803d',
                                          marginBottom: 8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6
                                        }}>
                                          <span>🟢</span> Verified Checkpoints ({verifiedCount})
                                        </div>
                                        {verifiedCount === 0 ? (
                                          <div style={{ fontSize: 12, color: '#94a3b8' }}>No checkpoints scanned yet.</div>
                                        ) : (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {p.verified.map(v => {
                                              const imgSrc = getImgSrc(v);
                                              return (
                                                <div
                                                  key={v.checkpoint_id}
                                                  style={{
                                                    background: '#f0fdf4',
                                                    border: '1px solid #bbf7d0',
                                                    borderRadius: 6,
                                                    padding: '8px 10px',
                                                    fontSize: 12,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8
                                                  }}
                                                >
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {imgSrc ? (
                                                      <img
                                                        src={imgSrc}
                                                        alt="Proof"
                                                        onClick={() => setPreviewImage(imgSrc)}
                                                        style={{
                                                          width: 42,
                                                          height: 32,
                                                          objectFit: 'cover',
                                                          borderRadius: 4,
                                                          border: '1px solid #86efac',
                                                          cursor: 'pointer'
                                                        }}
                                                        title="Click to zoom photo"
                                                      />
                                                    ) : (
                                                      <span style={{ fontSize: 13 }}>🟢</span>
                                                    )}
                                                    <div>
                                                      <div><strong>#{v.sequence_order}</strong> {v.name}</div>
                                                      {v.description && (
                                                        <div style={{ fontSize: 10.5, color: '#166534', opacity: 0.8 }}>{v.description}</div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div style={{ textAlign: 'right' }}>
                                                    <div style={{ color: '#166534', fontSize: 11, fontWeight: 600 }}>
                                                      {v.scanned_at ? fmtTimeSec(v.scanned_at) : ''} IST
                                                    </div>
                                                    {v.distance_from_checkpoint != null && (
                                                      <div style={{ color: '#64748b', fontSize: 10 }}>
                                                        {Math.round(v.distance_from_checkpoint)}m variance
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>

                                      {/* Missed List */}
                                      <div>
                                        <div style={{
                                          fontSize: 13,
                                          fontWeight: 700,
                                          color: '#be123c',
                                          marginBottom: 8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6
                                        }}>
                                          <span>🔴</span> Missed Checkpoints ({missedCount})
                                        </div>
                                        {missedCount === 0 ? (
                                          <div style={{ fontSize: 12, color: '#16a34a' }}>
                                            ✅ All checkpoints on this route were completed!
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {p.missed.map(m => (
                                              <div
                                                key={m.checkpoint_id}
                                                style={{
                                                  background: '#fff1f2',
                                                  border: '1px solid #fecdd3',
                                                  borderRadius: 6,
                                                  padding: '8px 10px',
                                                  fontSize: 12,
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  gap: 8
                                                }}
                                              >
                                                <div>
                                                  <div style={{ color: '#9f1239' }}>
                                                    <strong>#{m.sequence_order}</strong> {m.name}
                                                  </div>
                                                  {m.description && (
                                                    <div style={{ fontSize: 10.5, color: '#be123c', opacity: 0.85 }}>{m.description}</div>
                                                  )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                  <span style={{
                                                    fontSize: 10,
                                                    background: '#fee2e2',
                                                    color: '#991b1b',
                                                    padding: '2px 7px',
                                                    borderRadius: 4,
                                                    textTransform: 'uppercase',
                                                    fontWeight: 700
                                                  }}>
                                                    ❌ {m.scan_type || 'Photo'} Missed
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {(!dailyData.patrols || dailyData.patrols.length === 0) && (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', padding: '30px 16px', color: '#94a3b8' }}>
                            No patrol sessions recorded on {fmtDayDate(selectedDate)} (IST).
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Incidents & Guards Breakdown Grid */}
              <div className="grid-2 mb-6">
                {/* Incidents */}
                <div className="card">
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>
                    🚨 Incidents Reported ({dailyData.incidents?.length || 0})
                  </div>
                  {(!dailyData.incidents || dailyData.incidents.length === 0) ? (
                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>
                      No security incidents reported on this date.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {dailyData.incidents.map(inc => (
                        <div
                          key={inc.id}
                          style={{
                            padding: '10px 12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong>{inc.title}</strong>
                            <span className={`badge ${inc.severity === 'critical' ? 'badge-danger' : 'badge-warning'}`}>
                              {inc.severity}
                            </span>
                          </div>
                          {inc.description && (
                            <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                              {inc.description}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                            By {inc.guard_name} • {inc.reported_at ? `${fmtTime(inc.reported_at)} IST` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Guard Performance */}
                <div className="card">
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>
                    👮 Active Guards on Duty ({dailyData.guards?.length || 0})
                  </div>
                  {(!dailyData.guards || dailyData.guards.length === 0) ? (
                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>
                      No guards logged patrols on this date.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {dailyData.guards.map(g => (
                        <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{g.patrol_count} patrol(s) completed</div>
                          </div>
                          <div>
                            <span className={`badge ${(g.avg_compliance || 0) >= 80 ? 'badge-success' : 'badge-danger'}`}>
                              {g.avg_compliance}% compliance
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: 30-DAY TRENDS & ANALYTICS
          ───────────────────────────────────────────────────────────── */}
      {activeTab === 'trends' && (
        <div>
          {/* Date Range Selector */}
          <div className="card mb-4" style={{ padding: '12px 18px' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Date Range (IST):</span>
              <input type="date" className="form-input" style={{ width: 150 }} value={from} onChange={e => setFrom(e.target.value)} />
              <span style={{ color: '#64748b' }}>to</span>
              <input type="date" className="form-input" style={{ width: 150 }} value={to} onChange={e => setTo(e.target.value)} />
              <button className="btn btn-primary btn-sm" onClick={loadTrends} disabled={trendsLoading}>
                {trendsLoading ? 'Loading...' : 'Generate Trends'}
              </button>
            </div>
          </div>

          {trendsData && (
            <>
              <div className="stats-grid mb-6">
                <div className="card"><div className="card-title">Total Patrols</div><div className="card-value">{trendsData.summary.total_patrols}</div></div>
                <div className="card"><div className="card-title">Completed</div><div className="card-value" style={{ color: '#22c55e' }}>{trendsData.summary.completed_patrols}</div></div>
                <div className="card"><div className="card-title">Avg Compliance</div><div className="card-value" style={{ color: '#4361ee' }}>{trendsData.summary.avg_compliance || 0}%</div></div>
                <div className="card"><div className="card-title">Active Guards</div><div className="card-value">{trendsData.summary.active_guards}</div></div>
              </div>

              <div className="grid-2 mb-6">
                <div className="card">
                  <div style={{ fontWeight: 600, marginBottom: 16 }}>Daily Patrols vs Compliance</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={trendsData.dailyStats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => fmtMd(d)} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="patrols" fill="#4361ee" name="Patrols" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="avg_compliance" fill="#22c55e" name="Compliance %" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <div style={{ fontWeight: 600, marginBottom: 16 }}>Guard Performance Ranking</div>
                  {trendsData.topGuards.length === 0 && <div style={{ color: '#999' }}>No completed patrols in this range</div>}
                  {trendsData.topGuards.map((g, i) => (
                    <div key={g.id} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                        <span>#{i + 1} {g.name}</span>
                        <span style={{ fontWeight: 600 }}>{g.avg_compliance}%</span>
                      </div>
                      <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8 }}>
                        <div style={{
                          width: `${g.avg_compliance}%`, height: '100%', borderRadius: 4,
                          background: g.avg_compliance >= 90 ? '#22c55e' : g.avg_compliance >= 70 ? '#f59e0b' : '#ef233c'
                        }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{g.patrol_count} patrols completed</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <div style={{ fontWeight: 600, marginBottom: 16 }}>Daily Breakdown Table</div>
                <div className="table-scroll"><table className="table">
                  <thead>
                    <tr><th>Date</th><th>Patrols</th><th>Avg Compliance</th><th>Rating</th></tr>
                  </thead>
                  <tbody>
                    {trendsData.dailyStats.map(d => (
                      <tr key={d.date}>
                        <td>{fmtDayDate(d.date)}</td>
                        <td>{d.patrols}</td>
                        <td>{d.avg_compliance}%</td>
                        <td>
                          <span className={`badge ${d.avg_compliance >= 90 ? 'badge-success' : d.avg_compliance >= 70 ? 'badge-warning' : 'badge-danger'}`}>
                            {d.avg_compliance >= 90 ? 'Excellent' : d.avg_compliance >= 70 ? 'Good' : 'Needs Improvement'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {trendsData.dailyStats.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>No data for selected range</td></tr>
                    )}
                  </tbody>
                </table></div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Daily Report Email Schedule & Configuration Modal */}
      <DailyReportScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
      />

      {/* Lightbox Photo Preview Modal */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'relative',
              background: '#0f172a',
              borderRadius: 8,
              padding: 12,
              maxWidth: '90vw',
              maxHeight: '90vh',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
            }}
          >
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: 'absolute',
                top: -12,
                right: -12,
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: 28,
                height: 28,
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              ✕
            </button>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '85vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 4,
                display: 'block'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
